// active-panel.ts — tracks the currently-open side info panel so it can be
// re-rendered from the (already-fresh) caster cache without a full PHP re-query.
//
// Two triggers re-render the open panel:
//   1. The websocket caster_update handler (caster-update.ts) — live updates from
//      combat / other clients; it calls window.ftRerenderOpenInfoPanel(casterId)
//      right after refreshing window.FateTide.casterCache.
//   2. The adjust buttons (luck / mana / vitality) — after their PHP write they
//      ping the fresh blob into cache (fetchAndCacheCaster) then re-render here.
// Either way there is NO full-panel PHP HTML swap ("hard refresh") anymore.

import type { CachedCaster } from './types';
import { replaceHydratedHtml } from '../shared/replace-hydrated-html';

declare global {
    interface Window {
        RuneIcon?: { setCatalog?(catalog: Record<string, unknown>): void };
        ftMountRuneIcons?: (root?: Document | Element) => void;
        ftRerenderOpenInfoPanel: (casterId?: string) => void;
        __ftBreakdownDrillArming?: boolean;
    }
}

type PanelRenderer = (caster: CachedCaster) => string;

interface ActivePanel {
    casterId: string;
    targetId: string;
    render: PanelRenderer;
    caster?: () => CachedCaster | undefined;
    paint?: PanelPaint;
}

let active: ActivePanel | null = null;

interface PanelInput {
    read(caster: CachedCaster): unknown;
    value: unknown;
}
interface PanelPaint {
    html: string;
    tree: DocumentFragment;
    inputs: PanelInput[];
    first?: ChildNode | null;
}

// Observe the renderer's EXISTING readers, including omitted fields and enumerated source rows.
// Store only their scalar results/shape, never a copy of the caster or the computed cache.
function readPanel(panel: ActivePanel, caster: CachedCaster): PanelPaint | undefined {
    const inputs = new Map<string, PanelInput>();
    const at = (root: unknown, path: PropertyKey[]): unknown => {
        for (const key of path) root = root !== null && typeof root === 'object' ? Reflect.get(root, key) : undefined;
        return root;
    };
    const shape = (value: unknown): string => value === null ? 'null' : Array.isArray(value) ? 'array' : typeof value;
    const observe = (path: PropertyKey[], mode: string, read: PanelInput['read']): void => {
        inputs.set(`${mode}:${JSON.stringify(path)}`, { read, value: read(caster) });
    };
    const wrap = (value: object, path: PropertyKey[]): object => new Proxy(value, {
        get(object, key) {
            const next = Reflect.get(object, key);
            if (typeof key === 'symbol' || typeof next === 'function') return next;
            const child = [...path, key];
            if (next !== null && typeof next === 'object') {
                const prototype = Object.getPrototypeOf(next);
                if (Object.isFrozen(next) || (prototype !== Object.prototype && prototype !== Array.prototype && prototype !== null)) {
                    observe(child, 'value', root => at(root, child));
                    return next;
                }
                observe(child, 'shape', root => shape(at(root, child)));
                return wrap(next, child);
            }
            observe(child, 'value', root => at(root, child));
            return next;
        },
        ownKeys(object) {
            observe(path, 'keys', root => {
                const current = at(root, path);
                return current !== null && typeof current === 'object' ? JSON.stringify(Object.keys(current)) : '';
            });
            return Reflect.ownKeys(object);
        },
        has(object, key) {
            if (typeof key !== 'symbol') observe([...path, key], 'has', root => {
                const current = at(root, path);
                return current !== null && typeof current === 'object' && key in current;
            });
            return Reflect.has(object, key);
        },
    });
    let html: string;
    try { html = panel.render(wrap(caster, []) as CachedCaster); }
    catch { return undefined; } // Unsupported observation must not prevent the ordinary raw render.
    const template = document.createElement('template');
    template.innerHTML = html;
    return { html, tree: template.content, inputs: [...inputs.values()] };
}

/** Patch authored cells; leave hydrated rune faces and user-edited controls alone unless their
 * authored input changed. Hydrate only newly inserted/changed rune islands. */
function patchPanel(target: HTMLElement, before: DocumentFragment, after: DocumentFragment, caster: CachedCaster): void {
    const hydrate: Element[] = [];
    const dispose = (node: Node): void => {
        if (!(node instanceof Element)) return;
        const page = window as Window & { destroyTomSelectIn?: (root: Element) => void; disposeBootstrapTooltipsWithin?: (root: Element) => void };
        page.destroyTomSelectIn?.(node);
        page.disposeBootstrapTooltipsWithin?.(node);
    };
    const insert = (parent: Node, next: Node, old: Node | null): void => {
        const node = next.cloneNode(true);
        if (old) { dispose(old); parent.replaceChild(node, old); }
        else parent.appendChild(node);
        if (node instanceof Element && (node.matches('.ft-rune-mount') || node.querySelector('.ft-rune-mount'))) hydrate.push(node);
    };
    const patch = (parent: Node, a: Node, b: Node, live: Node): void => {
        if (a.isEqualNode(b)) return;
        if (a.nodeName !== b.nodeName || a.nodeType !== b.nodeType) { insert(parent, b, live); return; }
        if (a instanceof Element && b instanceof Element && live instanceof Element) {
            if (a.matches('.ft-rune-mount, .ft-icon')) { insert(parent, b, live); return; }
            for (const attr of a.attributes) if (!b.hasAttribute(attr.name)) live.removeAttribute(attr.name);
            for (const attr of b.attributes) if (a.getAttribute(attr.name) !== attr.value) {
                live.setAttribute(attr.name, attr.value);
                if (attr.name === 'value' && live instanceof HTMLInputElement) live.value = attr.value;
            }
            children(live, a, b);
        } else if (a.nodeValue !== b.nodeValue) live.nodeValue = b.nodeValue;
    };
    const children = (parent: Node, a: Node, b: Node): void => {
        const previous = [...a.childNodes], next = [...b.childNodes], live = [...parent.childNodes];
        for (let i = 0; i < Math.max(previous.length, next.length); i++) {
            const old = previous[i], value = next[i], node = live[i];
            if (!value) { if (node) { dispose(node); node.remove(); } }
            else if (!old || !node) insert(parent, value, null);
            else patch(parent, old, value, node);
        }
    };
    children(target, before, after);
    for (const island of hydrate) hydratePanelRunes(island, caster);
}

function mountPanel(panel: ActivePanel, target: HTMLElement, caster: CachedCaster, paint: PanelPaint | undefined): void {
    replaceHydratedHtml(target, paint ? paint.html : panel.render(caster));
    if (paint) paint.first = target.firstChild;
    panel.paint = paint;
    hydratePanelRunes(target, caster);
}

/**
 * Hydrate any rune sigils a panel's source-line blocks emitted (.ft-rune-mount placeholders).
 * Stamp the caster ROOT catalog on the engine FIRST so name1() resolves bare-UUID reference
 * layers (the mount blobs ship no catalog of their own); then run the footer scanner over the
 * freshly-injected subtree. Mirrors creature-console.ts's mount path. Safe no-op if the rune
 * bundle isn't loaded yet — the footer DOMContentLoaded pass covers initial render.
 */
function hydratePanelRunes(target: Element, caster: CachedCaster): void {
    try {
        const cat = caster.catalog;
        if (cat && typeof cat === 'object' && Object.keys(cat).length) {
            window.RuneIcon?.setCatalog?.(cat);
        }
    } catch { /* engine not ready */ }
    try { window.ftMountRuneIcons?.(target); } catch { /* footer pass covers init */ }
}

/** Called by each panel's entry function once it renders, so we know what's open. */
export function setActiveInfoPanel(casterId: string, targetId: string, render: PanelRenderer, caster?: ActivePanel['caster']): void {
    active = { casterId, targetId, render, caster };
    // Older entries still perform their own initial assignment immediately after registration.
    // Observe that mount once; subsequent cache ticks compare inputs before invoking the builder.
    const panel = active;
    queueMicrotask(() => {
        if (active !== panel || panel.paint) return;
        const caster = panel.caster ? panel.caster() : window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
        const target = document.getElementById(targetId);
        if (caster && target) {
            panel.paint = readPanel(panel, caster);
            if (!panel.paint) return;
            const authored = [...panel.paint.tree.childNodes];
            if (authored.length === target.childNodes.length && authored.every((node, i) => node.isEqualNode(target.childNodes[i]!))) {
                panel.paint.first = target.firstChild;
            }
        }
    });
}

/**
 * Inject the active panel's HTML once and hydrate its runes. Used for the FIRST paint so the
 * initial render goes through the same inject+hydrate path as a WS re-render (below). Call
 * setActiveInfoPanel() first; this reads `active`.
 */
export function paintActiveInfoPanel(caster: CachedCaster): void {
    if (!active) return;
    const target = document.getElementById(active.targetId);
    if (!target) return;
    // Opening a fresh breakdown invalidates any pending "Back to the previous breakdown" return.
    clearBreakdownReturn();
    mountPanel(active, target, caster, readPanel(active, caster));
}

export function clearActiveInfoPanel(): void {
    active = null;
}

/**
 * Re-render the open side info panel from the cache. No DB ping — the caller is
 * responsible for the cache being current (WS push already wrote it, or the
 * adjust button pinged it back first). A casterId argument scopes the refresh:
 * a WS update for a different creature than the one on screen is ignored.
 */
export function rerenderActiveInfoPanel(casterId?: string): void {
    if (!active) return;
    if (casterId && casterId !== active.casterId) return;
    // A live canvas MINIGAME owns the side-info drawer (lockpick — claimSideInfoForMinigame in
    // includes/js-src/lockpick/lockpick.ts). A breakdown left registered here from before the
    // minigame opened would repaint over its canvas on the next websocket caster update, and the
    // minigame reads a detached canvas as "panel replaced" → it CANCELS the attempt server-side.
    // Same failure the other automatic side-info writers are guarded against (2026-07-24).
    if (
        active.targetId === 'div_refresh_side_info'
        && (window as Window & { __ftSideInfoMinigameActive?: boolean }).__ftSideInfoMinigameActive
    ) return;
    const target = document.getElementById(active.targetId);
    if (!target) return;
    const caster = active.caster ? active.caster() : window.FateTide?.casterCache?.get(active.casterId) as CachedCaster | undefined;
    if (!caster) return;
    const previous = active.paint;
    if (!previous || previous.first !== target.firstChild) { mountPanel(active, target, caster, readPanel(active, caster)); return; }
    if (previous.inputs.every(input => Object.is(input.value, input.read(caster)))) return;
    const next = readPanel(active, caster);
    if (!next) { mountPanel(active, target, caster, next); return; }
    patchPanel(target, previous.tree, next.tree, caster);
    next.first = target.firstChild;
    active.paint = next;
}

window.ftRerenderOpenInfoPanel = rerenderActiveInfoPanel;

// ── Breakdown drill-in / Back-to-breakdown ──────────────────────────────────────────────────
// A breakdown panel (essence / speed / AC / attacks …) is an OFF-HISTORY view: it replaces the
// side-info HTML without pushing onto searchHistoryItemSearches (template.js). So when a source
// line drills into a contributing item (openItemInfo, which DOES push), the top-bar Back arrow
// would walk the item stack and skip right past the breakdown. We snapshot the breakdown here at
// drill time; template.js's nav state machine calls ftRestoreBreakdownReturn() to bring it back
// (same "intercept Back for a non-item view" model as the rune-preview overlay).
let breakdownReturn: ActivePanel | null = null;

/** Snapshot whatever breakdown panel is on screen NOW, so Back can restore it after a drill-in. */
function captureBreakdownReturn(): void {
    breakdownReturn = active ? { casterId: active.casterId, targetId: active.targetId, render: active.render, caster: active.caster } : null;
}
/** True when there is a captured breakdown to return to. */
function hasBreakdownReturn(): boolean {
    return breakdownReturn !== null;
}
/** Drop the captured breakdown (a fresh, unrelated search/open invalidates it). */
function clearBreakdownReturn(): void {
    breakdownReturn = null;
}
/**
 * Re-render the captured breakdown into its target (re-reading the live cache, like an off-history
 * overlay). Re-arms it as the `active` panel so subsequent WS updates re-render the breakdown.
 * Returns true on a clean restore; false (caller falls back to normal history) on a miss.
 */
function restoreBreakdownReturn(): boolean {
    if (!breakdownReturn) return false;
    const target = document.getElementById(breakdownReturn.targetId);
    if (!target) return false;
    const caster = breakdownReturn.caster ? breakdownReturn.caster() : window.FateTide?.casterCache?.get(breakdownReturn.casterId) as CachedCaster | undefined;
    if (!caster) return false;
    active = breakdownReturn;
    mountPanel(active, target, caster, readPanel(active, caster));
    return true;
}

/**
 * Drill from a breakdown source line into a contributing item. Snapshots the breakdown for Back,
 * arms the nav state machine (template.js reads __ftBreakdownDrillArming inside addToSearchHistory
 * to record the index the item lands on), then opens the item (cache-first — no DB ping when the
 * item is full-nested in the caster blob). Inline-callable: window.ftDrillFromBreakdown(href).
 */
function ftDrillFromBreakdown(href: string): void {
    try { captureBreakdownReturn(); } catch { /* nothing to capture */ }
    window.__ftBreakdownDrillArming = true;
    try {
        if (typeof openItemInfo === 'function') {
            openItemInfo(href);
        }
    } catch (e) {
        window.__ftBreakdownDrillArming = false;
        clearBreakdownReturn();
    }
}

Object.assign(window, {
    ftDrillFromBreakdown,
    ftHasBreakdownReturn: hasBreakdownReturn,
    ftClearBreakdownReturn: clearBreakdownReturn,
    ftRestoreBreakdownReturn: restoreBreakdownReturn,
});
